/* eslint-disable unicorn/no-null -- Tauri bridge helpers return `null` when running outside the desktop shell. */

type TauriInvokeFunction = <T>(command: string, arguments_?: Record<string, unknown>) => Promise<T>;
type TauriListenFunction = <T>(event: string, handler: (payload: { payload: T }) => void) => Promise<() => void>;

interface TauriEventModule {
  listen: TauriListenFunction;
}

interface TauriCoreModule {
  invoke: TauriInvokeFunction;
}

interface TauriGlobal {
  core?: TauriCoreModule;
  event?: TauriEventModule;
}

declare global {
  interface Window {
    __TAURI__?: TauriGlobal;
  }
}

function resolveTauriGlobal(): TauriGlobal | null {
  return globalThis.window?.__TAURI__ ?? null;
}

export function hasTauriBridge(): boolean {
  const tauri = resolveTauriGlobal();
  return Boolean(tauri?.core?.invoke);
}

export function tauriInvoke<T>(command: string, arguments_?: Record<string, unknown>): Promise<T> {
  const tauri = resolveTauriGlobal();
  const invoke = tauri?.core?.invoke;

  if (!invoke) {
    throw new Error('Tauri bridge unavailable');
  }

  return invoke<T>(command, arguments_);
}

export async function tauriListen<T>(event: string, handler: (payload: T) => void): Promise<() => void> {
  const tauri = resolveTauriGlobal();
  const listen = tauri?.event?.listen;

  if (!listen) {
    throw new Error('Tauri event bridge unavailable');
  }

  const unlisten = await listen<T>(event, (message) => {
    handler(message.payload);
  });

  return unlisten;
}
